<template>
  <div class="recategorisation-menu">
    <button :disabled="isDisabled" @click="toggleMenu">
      <span
        v-if="bulkRecategorisation"
        :id="`button-${_uid}`"
        class="i-text-body-2 i-text-neutral-120 i-py-3 i-pl-7 i-pr-4 i-radius-md d-flex justify-content-between align-items-center i-gap-6"
        :class="{
          'bulk-recategorisation-button': bulkRecategorisation,
        }"
      >
        Change category to...
        <i-icon
          symbol="medium-chevron-right"
          :size="16"
          :colour="palette['light-neutral-120']"
        />
      </span>
      <i-badge
        v-else
        :id="`button-${_uid}`"
        :class="{
          empty: !transactionCategory || !getCategoryLabel(transactionCategory),
        }"
        class="recategorisation-menu__pill"
        :label="
          transactionCategory
            ? getCategoryLabel(transactionCategory)
            : 'Category'
        "
        :interactable="!isDisabled"
        has-trailing-icon
        icon="small-chevron-down"
        @right-icon-click="toggleMenu"
      />
    </button>
    <div
      :id="`popover-${_uid}`"
      class="recategorisation-menu__popover recategorisation-menu-popover"
      tabindex="-1"
      :class="{ open: showMenu }"
    >
      <div
        :id="`parent-categories-${_uid}`"
        class="recategorisation-menu__categories d-flex flex-column"
      >
        <div
          v-for="parentCategory in transactionsCategories"
          :key="parentCategory.category_id"
          class="d-flex justify-content-between align-items-center"
        >
          <button
            class="i-text-neutral-120 i-text-body-3 i-pl-6 i-pr-3 i-py-4 i-radius-md d-flex justify-content-between flex-grow-1"
            :class="{
              selected: transactionCategory === parentCategory.category_id,
            }"
            @click="updateTransactionCategory(parentCategory.category_id)"
          >
            {{ getCategoryLabel(parentCategory.category_id) }}

            <i-icon
              v-if="transactionCategory === parentCategory.category_id"
              symbol="small_check_1"
              :size="16"
              :colour="palette['light-neutral-110']"
            />
          </button>

          <i-button
            v-if="parentCategory.subcategories?.length > 0"
            class="i-mr-2"
            left-icon="small-chevron-right"
            :size="iButtonSizes.SMALL"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.GHOST"
            @button-click="toggleChildCategoryMenu(parentCategory.category_id)"
          />

          <div
            v-if="parentCategory.subcategories?.length > 0"
            tabindex="-1"
            class="recategorisation-menu__childcategorymenu i-p-xs"
            :class="{
              open: selectedParentCategory === parentCategory.category_id,
            }"
          >
            <div class="d-flex align-items-center heading i-pb-4">
              <i-button
                left-icon="small-arrows-left-small"
                class="i-mr-2"
                :size="iButtonSizes.SMALL"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.GHOST"
                @button-click="
                  toggleChildCategoryMenu(parentCategory.category_id)
                "
              />
              <span class="i-text-neutral-120 i-text-body-3">{{
                getCategoryLabel(parentCategory.category_id)
              }}</span>
            </div>
            <div class="divider i-mx-4"></div>
            <div class="subcategories-list d-flex flex-column">
              <div
                v-for="childCategory in parentCategory.subcategories"
                :key="childCategory.category_id"
                class="d-flex align-items-center"
              >
                <button
                  class="i-radius-md i-pl-6 i-pr-3 i-py-4 i-radius-md flex-grow-1"
                  :class="{
                    selected: transactionCategory === childCategory.category_id,
                  }"
                  @click="updateTransactionCategory(childCategory.category_id)"
                >
                  <span class="i-text-neutral-120 i-text-body-3">{{
                    getCategoryLabel(childCategory.category_id)
                  }}</span>
                </button>

                <i-button
                  v-if="childCategory.subcategories?.length > 0"
                  class="i-mr-2"
                  left-icon="small-chevron-right"
                  :size="iButtonSizes.SMALL"
                  :type="iButtonTypes.NEUTRAL"
                  :button-style="iButtonStyles.GHOST"
                  @button-click="
                    toggleGrandchildCategoryMenu(childCategory.category_id)
                  "
                />
                <div
                  v-if="childCategory.subcategories?.length > 0"
                  tabindex="-1"
                  class="recategorisation-menu__childcategorymenu i-p-xs grandchild"
                  :class="{
                    open: selectedChildCategory === childCategory.category_id,
                  }"
                >
                  <div class="d-flex align-items-center heading i-pb-4">
                    <i-button
                      left-icon="small-arrows-left-small"
                      class="i-mr-2"
                      :size="iButtonSizes.SMALL"
                      :type="iButtonTypes.NEUTRAL"
                      :button-style="iButtonStyles.GHOST"
                      @button-click="
                        toggleGrandchildCategoryMenu(childCategory.category_id)
                      "
                    />
                    <span class="i-text-neutral-120 i-text-body-3">{{
                      getCategoryLabel(childCategory.category_id)
                    }}</span>
                  </div>
                  <div class="divider i-mx-4"></div>
                  <div class="subcategories-list d-flex flex-column">
                    <button
                      v-for="grandchildCategory in childCategory.subcategories"
                      :key="grandchildCategory.category_id"
                      class="i-radius-md i-pl-lg i-pr-3 i-py-4"
                      :class="{
                        selected:
                          transactionCategory ===
                          grandchildCategory.category_id,
                      }"
                      @click="
                        updateTransactionCategory(
                          grandchildCategory.category_id,
                        )
                      "
                    >
                      <span class="i-text-neutral-120 i-text-body-3">{{
                        getCategoryLabel(grandchildCategory.category_id)
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          class="i-text-neutral-120 i-text-body-3 i-pl-6 i-pr-3 i-py-4 i-radius-md d-flex justify-content-between flex-grow-1"
          :class="{
            selected: transactionCategory === null,
          }"
          @click="updateTransactionCategory('NULL')"
        >
          N/A (No Category)

          <i-icon
            v-if="transactionCategory === null"
            symbol="small_check_1"
            :size="16"
            :colour="palette['light-neutral-110']"
          />
        </button>
      </div>
    </div>

    <div
      v-if="showMenu"
      class="recategorisation-menu__overlay"
      @click="toggleMenu"
    ></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  createPopperLite as createPopper,
  preventOverflow,
  flip,
  // eslint-disable-next-line no-unused-vars
  offset,
} from '@popperjs/core';
import {
  iBadge,
  iBadgeColours,
  iIcon,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iMenuTriggerAlignOptions,
  positionMenu,
  showToast,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import creditTransactionsCategories from './CreditTransactionsCategories.content';
import { normalizeText } from '@/V2/credit/granular-insights/components/components.utils';
import { $event } from '@/bus';

export default {
  components: {
    iBadge,
    iIcon,
    iButton,
  },
  props: {
    transactionCategory: {
      type: String,
      default: null,
    },
    transactionIds: {
      type: Array,
      default: () => [],
    },
    bulkRecategorisation: {
      type: Boolean,
      default: false,
    },
    bulkRecategorisationMenuOpenState: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iBadgeColours,
      showMenu: false,
      creditTransactionsCategories,
      palette,
      selectedParentCategory: null,
      selectedChildCategory: null,
      selectedLevel: null,
      originalParentEl: null,
      buttonElement: null,
      popoverElement: null,
      iMenuTriggerAlignOptions,
    };
  },
  computed: {
    ...mapGetters(['transactionsCategories']),
  },
  watch: {
    bulkRecategorisationMenuOpenState: {
      deep: true,
      handler(openState) {
        if (this.bulkRecategorisation) {
          this.setMenuOpen(openState);
        }
      },
    },
  },
  mounted() {
    this.buttonElement = document.querySelector(`#button-${this._uid}`);
    this.popoverElement = document.querySelector(`#popover-${this._uid}`);
    this.originalParentEl = this.$el.parentNode;

    createPopper(this.buttonElement, this.popoverElement, {
      placement: this.bulkRecategorisation ? 'right-end' : 'bottom-start',
      modifiers: [
        preventOverflow,
        flip,
        {
          name: 'offset',
          options: {
            offset: [0, -30], // Adjust the values as needed (horizontal, vertical)
          },
        },
      ],
    });
  },
  methods: {
    ...mapActions(['setCustomerTransactionCategory']),

    toggleMenu(event) {
      if (event) {
        event.stopPropagation();
      }
      this.selectedParentCategory = null;
      this.selectedChildCategory = null;
      this.selectedLevel = null;
      this.resetScroll();
      const category = this.findParentCategory(
        this.transactionCategory,
        this.transactionsCategories,
      );

      if (this.selectedLevel === 'child') {
        this.selectedParentCategory = category;
      } else if (this.selectedLevel === 'grandchild') {
        const parent = this.findParentCategory(
          category,
          this.transactionsCategories,
        );
        this.selectedParentCategory = parent;
        this.selectedChildCategory = category;
      }

      this.setMenuOpen(!this.showMenu);

      if (this.bulkRecategorisation) {
        this.$emit('menu-toggle', this.showMenu);
      }
    },

    setMenuOpen(value) {
      this.showMenu = value;
      if (this.showMenu) {
        document.body.appendChild(this.popoverElement);
        positionMenu(
          this.buttonElement,
          this.popoverElement,
          iMenuTriggerAlignOptions.LEFT,
        );
      } else {
        this.originalParentEl?.appendChild(this.popoverElement);
      }
    },

    getCategoryLabel(category) {
      return (
        creditTransactionsCategories[category]?.label || normalizeText(category)
      );
    },

    toggleChildCategoryMenu(categoryKey) {
      this.selectedParentCategory =
        this.selectedParentCategory === categoryKey ? null : categoryKey;
    },

    toggleGrandchildCategoryMenu(categoryKey) {
      this.selectedChildCategory =
        this.selectedChildCategory === categoryKey ? null : categoryKey;
    },

    findParentCategory(categoryId, categoriesList) {
      if (!categoriesList) {
        return;
      }

      for (const parentCategory of categoriesList) {
        if (parentCategory.subcategories !== null) {
          for (const childCategory of parentCategory.subcategories) {
            if (childCategory.category_id === categoryId) {
              this.selectedLevel = 'child';
              return parentCategory.category_id;
            }

            // Check grandchild categories
            if (childCategory.subcategories !== null) {
              for (const grandchildCategory of childCategory.subcategories) {
                if (grandchildCategory.category_id === categoryId) {
                  this.selectedLevel = 'grandchild';
                  return childCategory.category_id;
                }
              }
            }
          }
        }
      }
      return null;
    },

    resetScroll() {
      const parentCategories = document.getElementById(
        `parent-categories-${this._uid}`,
      );
      if (parentCategories) {
        parentCategories.scrollTop = 0;
      }
    },

    async updateTransactionCategory(category) {
      try {
        await this.setCustomerTransactionCategory({
          customerId: this.$route.params.customer_id,
          transactionIds: this.transactionIds,
          category,
        });
        this.setMenuOpen(false);
        $event.$emit('credit:refresh', {});
        this.$emit('categories-updated');
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'There was an error updating the transaction category. Please try again.';
        showToast({
          text: errorMessage,
          isError: true,
        });
      }
    },
  },
};
</script>

<style lang="scss" scope>
$menu-width: 192px;
$menu-height: 310px;
$menu-padding: 8px;

.recategorisation-menu {
  position: relative;
  display: flex;

  button {
    border: none;
    background: none;
    cursor: pointer;
    margin: 0;
    .i-badge {
      &.empty {
        border: 1px solid $light-neutral-70;
        color: $light-neutral-90;
        background: white;
      }
    }

    .bulk-recategorisation-button {
      background: $light-neutral-30;

      &:active,
      &:hover {
        background: $light-neutral-40;
      }
    }
  }

  &__pill {
    white-space: nowrap;
  }

  &__categories {
    overflow-y: auto;
    overflow-x: hidden;
    height: $menu-height - ($menu-padding * 2);
  }

  &__childcategorymenu {
    position: absolute;
    z-index: 1;
    top: $menu-padding;
    right: -$menu-width;
    width: $menu-width - ($menu-padding * 2);
    height: $menu-height - ($menu-padding * 2);
    background-color: $light-neutral-default;
    word-wrap: break-word;
    transition: right 0.3s ease-in-out;

    &.open {
      right: $menu-padding;
    }

    &.grandchild {
      top: 0;
      right: -$menu-width;

      &.open {
        right: 0;
      }
    }

    .divider {
      border-bottom: 1px solid $light-neutral-50;
    }

    .subcategories-list {
      overflow-y: auto;
      overflow-x: hidden;
      height: $menu-height - ($menu-padding * 2) - 40px;
    }

    button {
      .i-badge {
        display: inline-flex !important;
      }
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
  }
}

.recategorisation-menu-popover {
  position: fixed;
  z-index: 99;
  width: $menu-width;
  height: $menu-height;
  overflow: hidden;
  background-color: $light-neutral-default;
  z-index: 110;
  visibility: hidden;
  opacity: 0;
  transition:
    visibility,
    opacity 0.15s ease-in-out;
  border-radius: $base-unit * 2;
  border: 1px solid $light-neutral-50;
  padding: $base-unit;
  box-shadow:
    0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);

  &.open {
    visibility: visible;
    opacity: 1;
  }

  button {
    text-align: left;
    border: none;
    background: $light-neutral-default;
    cursor: pointer;

    margin: 0;

    &:active,
    &:hover {
      background: $light-neutral-30;
    }

    &.selected {
      background: $light-neutral-20;
      &:active,
      &:hover {
        background: $light-neutral-30;
      }
    }

    .bulk-recategorisation-button {
      background: $light-neutral-30;

      &:active,
      &:hover {
        background: $light-neutral-40;
      }
    }
  }
}
</style>
